import {
  Building2,
  ChevronDown,
  Clock,
  FileText,
  Filter,
  Layers,
  Mail,
  MessageSquarePlus,
  Phone,
  Plus,
  Search,
  Star,
  Tag,
  Trash2,
  User,
  UserPlus,
  X,
  Zap
} from 'lucide-react';
import { useEffect, useMemo, useState } from 'react';
import { getProfile } from '../../config/activityProfileConfig2';
import useGrandeEcole from '../../stores/useGrandeEcole';

// ─── Palette couleurs étapes ──────────────────────────────────────────────────
const COLOR_MAP = {
  zinc:    { dot:'bg-zinc-500',    text:'text-zinc-400',    border:'border-zinc-700/80',    bg:'bg-zinc-900/40',    pill:'bg-zinc-800 text-zinc-300',         ring:'ring-zinc-700'    },
  sky:     { dot:'bg-sky-500',     text:'text-sky-400',     border:'border-sky-800/60',     bg:'bg-sky-950/20',     pill:'bg-sky-900/60 text-sky-300',         ring:'ring-sky-800'     },
  amber:   { dot:'bg-amber-500',   text:'text-amber-400',   border:'border-amber-800/60',   bg:'bg-amber-950/20',   pill:'bg-amber-900/50 text-amber-300',     ring:'ring-amber-800'   },
  orange:  { dot:'bg-orange-500',  text:'text-orange-400',  border:'border-orange-800/60',  bg:'bg-orange-950/20',  pill:'bg-orange-900/50 text-orange-300',   ring:'ring-orange-800'  },
  emerald: { dot:'bg-emerald-500', text:'text-emerald-400', border:'border-emerald-800/60', bg:'bg-emerald-950/20', pill:'bg-emerald-900/50 text-emerald-300', ring:'ring-emerald-800' },
  fuchsia: { dot:'bg-fuchsia-500', text:'text-fuchsia-400', border:'border-fuchsia-800/60', bg:'bg-fuchsia-950/20', pill:'bg-fuchsia-900/50 text-fuchsia-300', ring:'ring-fuchsia-800' },
  purple:  { dot:'bg-purple-500',  text:'text-purple-400',  border:'border-purple-800/60',  bg:'bg-purple-950/20',  pill:'bg-purple-900/50 text-purple-300',   ring:'ring-purple-800'  },
  red:     { dot:'bg-red-500',     text:'text-red-400',     border:'border-red-800/60',     bg:'bg-red-950/20',     pill:'bg-red-900/50 text-red-300',         ring:'ring-red-800'     },
};
const sc = (color, key) => COLOR_MAP[color]?.[key] ?? COLOR_MAP.zinc[key];

// ─── Accent dynamique depuis profile.accentColor ─────────────────────────────
const ACCENT = {
  orange:  { btn:'bg-orange-600 hover:bg-orange-500',  text:'text-orange-300',  border:'border-orange-900/40',  bg:'bg-orange-950/20',  tab:'border-orange-500 text-orange-300 bg-orange-950/15',  badge:'bg-orange-500',  range:'accent-orange-500',  note:'bg-orange-950/40 border-orange-800/40 text-orange-400 hover:bg-orange-950/60'  },
  sky:     { btn:'bg-sky-700 hover:bg-sky-600',         text:'text-sky-300',     border:'border-sky-900/40',     bg:'bg-sky-950/20',     tab:'border-sky-500 text-sky-300 bg-sky-950/15',            badge:'bg-sky-500',     range:'accent-sky-500',     note:'bg-sky-950/40 border-sky-800/40 text-sky-400 hover:bg-sky-950/60'             },
  emerald: { btn:'bg-emerald-700 hover:bg-emerald-600', text:'text-emerald-300', border:'border-emerald-900/40', bg:'bg-emerald-950/20', tab:'border-emerald-500 text-emerald-300 bg-emerald-950/15',badge:'bg-emerald-500', range:'accent-emerald-500', note:'bg-emerald-950/40 border-emerald-800/40 text-emerald-400 hover:bg-emerald-950/60'},
  fuchsia: { btn:'bg-fuchsia-700 hover:bg-fuchsia-600', text:'text-fuchsia-300', border:'border-fuchsia-900/40', bg:'bg-fuchsia-950/20', tab:'border-fuchsia-500 text-fuchsia-300 bg-fuchsia-950/15',badge:'bg-fuchsia-500', range:'accent-fuchsia-500', note:'bg-fuchsia-950/40 border-fuchsia-800/40 text-fuchsia-400 hover:bg-fuchsia-950/60'},
  purple:  { btn:'bg-purple-700 hover:bg-purple-600',   text:'text-purple-300',  border:'border-purple-900/40',  bg:'bg-purple-950/20',  tab:'border-purple-500 text-purple-300 bg-purple-950/15',   badge:'bg-purple-500',  range:'accent-purple-500',  note:'bg-purple-950/40 border-purple-800/40 text-purple-400 hover:bg-purple-950/60'  },
};
const ac = (accentColor, key) => ACCENT[accentColor]?.[key] ?? ACCENT.orange[key];

// ─── ScoreBar ─────────────────────────────────────────────────────────────────
function ScoreBar({ score, size = 'md' }) {
  const bar  = score >= 80 ? 'bg-emerald-500' : score >= 50 ? 'bg-amber-500' : 'bg-red-500';
  const text = score >= 80 ? 'text-emerald-400' : score >= 50 ? 'text-amber-400' : 'text-red-400';
  return (
    <div className={`flex items-center gap-2 ${size === 'sm' ? 'w-20' : 'w-28'}`}>
      <div className="flex-1 h-1 bg-zinc-800/80 overflow-hidden">
        <div className={`h-full ${bar} transition-all duration-500`} style={{ width: `${Math.min(100, score)}%` }} />
      </div>
      <span className={`text-[10px] font-black font-mono ${text} w-6 text-right tabular-nums`}>{score}</span>
    </div>
  );
}

// ─── ContactCard ──────────────────────────────────────────────────────────────
function ContactCard({ contact, profile, accentColor, onClick, onMove, onDelete, compact }) {
  const stages     = profile.crmConfig.pipelineStages;
  const currentIdx = stages.findIndex(s => s.id === contact.stage);

  if (compact) {
    // Mode compact : carte condensée sur 1 ligne
    return (
      <div
        onClick={() => onClick(contact)}
        className="group flex items-center gap-2 px-2.5 py-2 border border-zinc-800/60 bg-zinc-900/40 hover:bg-zinc-900 hover:border-zinc-700/60 transition-all cursor-pointer"
      >
        <div className={`w-1.5 h-1.5 rounded-full flex-shrink-0 ${sc(stages[currentIdx]?.color ?? 'zinc', 'dot')}`} />
        <div className="flex-1 min-w-0">
          <div className="text-[10px] font-bold text-zinc-200 truncate leading-none">
            {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
          </div>
          {contact.entreprise && (
            <div className="text-[8px] text-zinc-600 font-mono truncate mt-0.5">{contact.entreprise}</div>
          )}
        </div>
        <div className="flex items-center gap-1 flex-shrink-0">
          <span className={`text-[9px] font-black font-mono tabular-nums ${
            contact.score >= 80 ? 'text-emerald-400' : contact.score >= 50 ? 'text-amber-400' : 'text-red-400'
          }`}>{contact.score ?? 0}</span>
          <button
            onClick={e => { e.stopPropagation(); onDelete(contact._id ?? contact.id); }}
            className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-700 hover:text-red-400 transition-all"
          >
            <X size={9} />
          </button>
        </div>
      </div>
    );
  }

  // Mode normal
  return (
    <div
      onClick={() => onClick(contact)}
      className="group cursor-pointer p-3 border border-zinc-800/70 bg-zinc-900/50 hover:bg-zinc-900 hover:border-zinc-700/70 transition-all space-y-2"
    >
      {/* Nom + suppr */}
      <div className="flex items-start justify-between gap-2">
        <div className="flex-1 min-w-0">
          <div className="text-[11px] font-black text-zinc-200 truncate leading-tight uppercase tracking-tight">
            {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
          </div>
          {contact.entreprise && (
            <div className="text-[9px] text-zinc-600 font-mono mt-0.5 flex items-center gap-1 truncate">
              <Building2 size={8} className="flex-shrink-0 text-zinc-700" />
              <span className="truncate">{contact.entreprise}</span>
            </div>
          )}
        </div>
        <button
          onClick={e => { e.stopPropagation(); onDelete(contact._id ?? contact.id); }}
          className="opacity-0 group-hover:opacity-100 p-0.5 text-zinc-700 hover:text-red-400 transition-all flex-shrink-0"
        >
          <X size={10} />
        </button>
      </div>

      <ScoreBar score={contact.score ?? 0} size="sm" />

      {/* Footer */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-1">
          {contact.email && (
            <a href={`mailto:${contact.email}`} onClick={e => e.stopPropagation()}
               className="p-1 bg-zinc-800/50 hover:bg-zinc-800 text-zinc-600 hover:text-zinc-300 transition-colors">
              <Mail size={9} />
            </a>
          )}
          {contact.telephone && (
            <a href={`tel:${contact.telephone}`} onClick={e => e.stopPropagation()}
               className="p-1 bg-zinc-800/50 hover:bg-zinc-800 text-zinc-600 hover:text-zinc-300 transition-colors">
              <Phone size={9} />
            </a>
          )}
        </div>
        {/* Navigation étape */}
        <div className="flex items-center gap-0.5">
          {currentIdx > 0 && (
            <button
              onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx - 1].id); }}
              className="w-5 h-5 flex items-center justify-center text-zinc-700 hover:text-zinc-300 transition-colors text-xs"
            >‹</button>
          )}
          {currentIdx < stages.length - 1 && (
            <button
              onClick={e => { e.stopPropagation(); onMove(contact._id ?? contact.id, stages[currentIdx + 1].id); }}
              className="w-5 h-5 flex items-center justify-center text-zinc-700 hover:text-emerald-400 transition-colors text-xs"
            >›</button>
          )}
        </div>
      </div>

      {/* Tags */}
      {contact.tags?.length > 0 && (
        <div className="flex flex-wrap gap-1">
          {contact.tags.slice(0, 3).map(tag => (
            <span key={tag} className="text-[8px] px-1 py-0.5 bg-zinc-800/70 text-zinc-500 font-mono">#{tag}</span>
          ))}
        </div>
      )}

      {contact.notes?.length > 0 && (
        <div className="text-[9px] text-zinc-700 flex items-center gap-1">
          <FileText size={8} /> {contact.notes.length}
        </div>
      )}
    </div>
  );
}

// ─── KanbanColumn ─────────────────────────────────────────────────────────────
function KanbanColumn({ stage, contacts, profile, accentColor, onSelect, onMove, onDelete }) {
  // 3 états : 'full' | 'compact' | 'collapsed'
  const [mode, setMode] = useState('full');

  const cycleMode = () => setMode(m => m === 'full' ? 'compact' : m === 'compact' ? 'collapsed' : 'full');

  const isEmpty = contacts.length === 0;

  return (
    <div className={`flex flex-col border ${sc(stage.color, 'border')} ${sc(stage.color, 'bg')} flex-shrink-0 transition-all duration-200 ${
      mode === 'collapsed' ? 'w-10' : mode === 'compact' ? 'w-44' : 'w-52'
    }`}>
      {/* Header colonne */}
      <button
        onClick={cycleMode}
        className={`flex items-center justify-between px-2.5 py-2.5 border-b ${sc(stage.color, 'border')} hover:bg-black/20 transition-colors group ${
          mode === 'collapsed' ? 'flex-col gap-2 h-full justify-start pt-3' : ''
        }`}
      >
        {mode === 'collapsed' ? (
          <>
            <span className="text-sm">{stage.icon}</span>
            <div className={`text-[8px] font-black uppercase tracking-widest ${sc(stage.color, 'text')} [writing-mode:vertical-rl] rotate-180 leading-none`}>
              {stage.label}
            </div>
            <span className={`text-[9px] font-black tabular-nums ${sc(stage.color, 'text')} mt-auto mb-1`}>
              {contacts.length}
            </span>
          </>
        ) : (
          <>
            <div className="flex items-center gap-1.5 min-w-0">
              <span className="text-sm flex-shrink-0">{stage.icon}</span>
              <span className={`text-[9px] font-black uppercase tracking-wider ${sc(stage.color, 'text')} truncate`}>
                {stage.label}
              </span>
            </div>
            <div className="flex items-center gap-1.5 flex-shrink-0">
              <span className={`text-[9px] font-black tabular-nums ${sc(stage.color, 'text')}`}>
                {contacts.length}
              </span>
              {/* Indicateur mode */}
              <div className={`flex gap-0.5 opacity-0 group-hover:opacity-100 transition-opacity`}>
                {['full','compact','collapsed'].map(m => (
                  <div key={m} className={`w-1 h-1 rounded-full transition-colors ${mode === m ? sc(stage.color, 'dot') : 'bg-zinc-700'}`} />
                ))}
              </div>
            </div>
          </>
        )}
      </button>

      {/* Cards */}
      {mode !== 'collapsed' && (
        <div className={`flex flex-col min-h-[3rem] overflow-y-auto scrollbar-none ${
          mode === 'compact' ? 'gap-0.5 p-1' : 'gap-1.5 p-1.5'
        }`}
          style={{ maxHeight: '32rem' }}
        >
          {contacts.map(ct => (
            <ContactCard
              key={ct._id ?? ct.id}
              contact={ct}
              profile={profile}
              accentColor={accentColor}
              onClick={onSelect}
              onMove={onMove}
              onDelete={onDelete}
              compact={mode === 'compact'}
            />
          ))}
          {isEmpty && (
            <div className={`text-center text-zinc-800 ${mode === 'compact' ? 'py-3 text-[8px]' : 'py-6 text-[9px]'}`}>
              —
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ─── ContactDrawer ────────────────────────────────────────────────────────────
function ContactDrawer({ contact, profile, accentColor, onClose, onUpdate, onAddNote, onMove }) {
  const [newNote, setNewNote]   = useState('');
  const [addingNote, setAdding] = useState(false);
  const [editScore, setEditScore] = useState(false);
  const [scoreVal, setScoreVal]   = useState(contact.score ?? 50);
  const stages      = profile.crmConfig.pipelineStages;
  const currentStage = stages.find(s => s.id === contact.stage) ?? stages[0];

  const handleNoteSubmit = async () => {
    if (!newNote.trim()) return;
    setAdding(true);
    await onAddNote(contact._id ?? contact.id, newNote.trim());
    setNewNote('');
    setAdding(false);
  };

  return (
    <div className="fixed inset-0 z-[90] flex justify-end">
      <div className="absolute inset-0 bg-black/65 backdrop-blur-sm" onClick={onClose} />
      <div className="relative w-full max-w-md bg-[#0d1117] border-l border-zinc-800/80 flex flex-col h-full shadow-2xl">

        {/* Header */}
        <div className={`flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-black/50 shrink-0`}>
          <div className="flex items-center gap-3">
            <div className={`w-9 h-9 flex items-center justify-center text-sm ${sc(currentStage.color, 'bg')} border ${sc(currentStage.color, 'border')}`}>
              {currentStage.icon}
            </div>
            <div>
              <div className="text-sm font-black text-zinc-100 uppercase tracking-tighter leading-tight">
                {contact.prenom ? `${contact.prenom} ${contact.nom}` : contact.nom}
              </div>
              <div className="flex items-center gap-2 mt-0.5">
                <span className={`text-[9px] font-mono uppercase tracking-wider ${sc(currentStage.color, 'text')}`}>
                  {currentStage.label}
                </span>
                {contact.entreprise && (
                  <>
                    <span className="text-zinc-700">·</span>
                    <span className="text-[9px] text-zinc-500 font-mono truncate max-w-[120px]">{contact.entreprise}</span>
                  </>
                )}
              </div>
            </div>
          </div>
          <button onClick={onClose} className="p-1.5 text-zinc-600 hover:text-white transition-colors">
            <X size={15} />
          </button>
        </div>

        <div className="flex-1 overflow-y-auto scrollbar-none divide-y divide-zinc-800/40">

          {/* Score */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
                {profile.crmConfig.scoreLabel}
              </span>
              <button onClick={() => setEditScore(!editScore)}
                      className={`text-[9px] font-mono transition-colors ${ac(accentColor, 'text')} hover:opacity-70`}>
                {editScore ? 'Valider ✓' : 'Modifier'}
              </button>
            </div>
            {editScore ? (
              <div className="flex items-center gap-3">
                <input type="range" min={0} max={100} value={scoreVal}
                       onChange={e => setScoreVal(Number(e.target.value))}
                       onMouseUp={() => onUpdate(contact._id ?? contact.id, { score: scoreVal })}
                       className={`flex-1 ${ac(accentColor, 'range')}`} />
                <span className={`text-sm font-black font-mono ${ac(accentColor, 'text')} w-8 tabular-nums`}>{scoreVal}</span>
              </div>
            ) : (
              <ScoreBar score={contact.score ?? 0} />
            )}
          </div>

          {/* Pipeline selector */}
          <div className="px-5 py-4 space-y-2">
            <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
              {profile.vocabulary.pipeline}
            </span>
            <div className="flex flex-wrap gap-1.5">
              {stages.map(stage => (
                <button
                  key={stage.id}
                  onClick={() => onMove(contact._id ?? contact.id, stage.id)}
                  className={`px-2 py-1 text-[9px] font-mono uppercase tracking-wide border transition-all ${
                    contact.stage === stage.id
                      ? `${sc(stage.color, 'bg')} ${sc(stage.color, 'border')} ${sc(stage.color, 'text')} font-black`
                      : 'bg-black/40 border-zinc-800 text-zinc-600 hover:text-zinc-300 hover:border-zinc-700'
                  }`}
                >
                  {stage.icon} {stage.label}
                </button>
              ))}
            </div>
          </div>

          {/* Coordonnées */}
          <div className="px-5 py-4">
            <div className="border border-zinc-800/60 bg-black/20 divide-y divide-zinc-800/40">
              {[
                { icon: Mail,      key: 'email',      label: 'Email',                      href: v => `mailto:${v}` },
                { icon: Phone,     key: 'telephone',  label: 'Tél.',                        href: v => `tel:${v}`    },
                { icon: Building2, key: 'entreprise', label: profile.vocabulary.company,    href: null               },
              ].filter(f => contact[f.key]).map(({ icon: Icon, key, label, href }) => (
                <div key={key} className="flex items-center gap-3 px-3 py-2">
                  <Icon size={12} className="text-zinc-700 flex-shrink-0" />
                  <span className="text-[9px] text-zinc-600 w-16 flex-shrink-0 uppercase tracking-wider">{label}</span>
                  {href ? (
                    <a href={href(contact[key])}
                       className={`text-[10px] ${ac(accentColor, 'text')} hover:opacity-70 truncate flex-1 font-mono underline underline-offset-2 transition-opacity`}>
                      {contact[key]}
                    </a>
                  ) : (
                    <span className="text-[10px] text-zinc-300 truncate flex-1 font-mono">{contact[key]}</span>
                  )}
                </div>
              ))}
            </div>
          </div>

          {/* Tags */}
          {contact.tags?.length > 0 && (
            <div className="px-5 py-3 space-y-2">
              <div className="flex items-center gap-2">
                <Tag size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">Tags</span>
              </div>
              <div className="flex flex-wrap gap-1">
                {contact.tags.map(tag => (
                  <span key={tag} className="text-[9px] px-1.5 py-0.5 bg-zinc-800/80 border border-zinc-700/60 text-zinc-500 font-mono">#{tag}</span>
                ))}
              </div>
            </div>
          )}

          {/* Notes */}
          <div className="px-5 py-4 space-y-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <FileText size={11} className="text-zinc-700" />
                <span className="text-[9px] uppercase tracking-widest text-zinc-600 font-mono">
                  {profile.crmConfig.noteLabel}
                </span>
              </div>
              <span className="text-[9px] text-zinc-700 font-mono tabular-nums">{contact.notes?.length ?? 0}</span>
            </div>

            <div className="flex gap-2">
              <textarea
                value={newNote}
                onChange={e => setNewNote(e.target.value)}
                placeholder="Nouvelle note…"
                rows={2}
                className="flex-1 bg-black/60 border border-zinc-800 p-2 text-[10px] text-zinc-300 placeholder-zinc-700 focus:border-zinc-600 outline-none resize-none font-mono"
              />
              <button
                onClick={handleNoteSubmit}
                disabled={!newNote.trim() || addingNote}
                className={`px-2 border ${ac(accentColor, 'note')} disabled:opacity-30 transition-colors self-end py-1`}
              >
                <MessageSquarePlus size={12} />
              </button>
            </div>

            <div className="space-y-1.5 max-h-48 overflow-y-auto scrollbar-none">
              {(contact.notes ?? []).map(note => (
                <div key={note.id} className="px-3 py-2 bg-zinc-900/50 border border-zinc-800/50 space-y-1">
                  <p className="text-[10px] text-zinc-300 leading-relaxed">{note.text}</p>
                  <div className="text-[9px] text-zinc-700 font-mono flex items-center gap-2">
                    <Clock size={8} />
                    {new Date(note.createdAt).toLocaleString('fr-FR')}
                    {note.author && <span>· {note.author}</span>}
                  </div>
                </div>
              ))}
              {!contact.notes?.length && (
                <p className="text-[10px] text-zinc-700 italic text-center py-2">Aucune note</p>
              )}
            </div>
          </div>

          {/* Dates */}
          <div className="px-5 py-3 text-[9px] text-zinc-700 font-mono space-y-0.5">
            <div>Créé : {new Date(contact.createdAt).toLocaleString('fr-FR')}</div>
            <div>Modifié : {new Date(contact.updatedAt).toLocaleString('fr-FR')}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

// ─── NewContactModal ──────────────────────────────────────────────────────────
function NewContactModal({ profile, accentColor, onClose, onCreate }) {
  const stages = profile.crmConfig.pipelineStages;
  const [form, setForm] = useState({
    nom: '', prenom: '', email: '', telephone: '', entreprise: '',
    stage: stages[0]?.id ?? 'prospect',
    score: 50,
    tags: '',
  });
  const [saving, setSaving] = useState(false);
  const f = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async () => {
    if (!form.nom.trim()) return;
    setSaving(true);
    await onCreate({
      ...form,
      tags: form.tags ? form.tags.split(',').map(t => t.trim()).filter(Boolean) : [],
    });
    setSaving(false);
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
      <div className="w-full max-w-md bg-zinc-950 border border-zinc-800 shadow-2xl">
        <div className="sticky top-0 flex items-center justify-between px-5 py-4 border-b border-zinc-800 bg-zinc-950/95">
          <div className="flex items-center gap-2">
            <div className={`w-1.5 h-1.5 rounded-full ${ac(accentColor, 'badge')} animate-pulse`} />
            <h3 className={`text-xs font-black uppercase tracking-tighter ${ac(accentColor, 'text')}`}>
              Nouveau {profile.vocabulary.contact}
            </h3>
          </div>
          <button onClick={onClose} className="text-zinc-600 hover:text-white transition-colors">
            <X size={14} />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <div className="grid grid-cols-2 gap-3">
            {[
              { k:'prenom',    label:'Prénom'     },
              { k:'nom',       label:'Nom *'      },
              { k:'email',     label:'Email'      },
              { k:'telephone', label:'Téléphone'  },
            ].map(({ k, label }) => (
              <div key={k}>
                <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">{label}</label>
                <input value={form[k]} onChange={e => f(k, e.target.value)}
                       className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
              </div>
            ))}
          </div>

          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">
              {profile.vocabulary.company}
            </label>
            <input value={form.entreprise} onChange={e => f('entreprise', e.target.value)}
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">Étape pipeline</label>
              <select value={form.stage} onChange={e => f('stage', e.target.value)}
                      className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 focus:border-zinc-600 outline-none font-mono">
                {stages.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
              </select>
            </div>
            <div>
              <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">
                {profile.crmConfig.scoreLabel} ({form.score})
              </label>
              <input type="range" min={0} max={100} value={form.score}
                     onChange={e => f('score', Number(e.target.value))}
                     className={`w-full ${ac(accentColor, 'range')} mt-2`} />
            </div>
          </div>

          <div>
            <label className="text-[9px] uppercase tracking-widest text-zinc-600 block mb-1">
              Tags (virgule)
            </label>
            <input value={form.tags} onChange={e => f('tags', e.target.value)}
                   placeholder="vip, relance, prioritaire…"
                   className="w-full bg-black/60 border border-zinc-800 px-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none font-mono" />
          </div>

          <button
            onClick={handleSubmit}
            disabled={!form.nom.trim() || saving}
            className={`w-full py-3 ${ac(accentColor, 'btn')} disabled:bg-zinc-800 disabled:text-zinc-600 text-white text-[10px] font-black uppercase tracking-widest transition-colors flex items-center justify-center gap-2`}
          >
            <UserPlus size={12} />
            {saving ? 'Création…' : `Créer ${profile.vocabulary.contact}`}
          </button>
        </div>
      </div>
    </div>
  );
}

// ─── CRMPanel — composant principal ──────────────────────────────────────────
export function CRMPanel({ darkMode = true }) {
  const {
    activityProfile,
    crmContacts, crmLoading,
    crmSearch, crmStageFilter,
    selectedContact,
    fetchCrmContacts, createCrmContact, updateCrmContact,
    deleteCrmContact, moveCrmContactStage, addCrmNote,
    setCrmSearch, setCrmStageFilter,
    setSelectedContact, clearSelectedContact,
    filteredCrmContacts,
  } = useGrandeEcole();

  const profile     = getProfile(activityProfile);
  const accentColor = profile.accentColor ?? 'orange';
  const stages      = profile.crmConfig.pipelineStages;

  const [view, setView]           = useState('kanban');
  const [showNew, setShowNew]     = useState(false);
  const [globalCompact, setGlobalCompact] = useState(false);

  const filtered = useMemo(
    () => filteredCrmContacts?.() ?? crmContacts,
    [crmContacts, crmSearch, crmStageFilter, filteredCrmContacts]
  );

  const byStage = useMemo(() => {
    const map = {};
    stages.forEach(s => { map[s.id] = []; });
    filtered.forEach(ct => {
      if (map[ct.stage]) map[ct.stage].push(ct);
      else map[stages[0]?.id]?.push(ct);
    });
    return map;
  }, [filtered, stages]);

  const kpis = useMemo(() => {
    const total     = crmContacts.length;
    const lastId    = stages.at(-1)?.id;
    const preLastId = stages.at(-2)?.id;
    const converted = preLastId ? crmContacts.filter(ct => ct.stage === preLastId).length : 0;
    const avgScore  = total > 0
      ? Math.round(crmContacts.reduce((a, ct) => a + (ct.score ?? 0), 0) / total)
      : 0;
    const actifs    = crmContacts.filter(ct => ct.stage !== lastId).length;
    return { total, converted, avgScore, actifs };
  }, [crmContacts, stages]);

  useEffect(() => { fetchCrmContacts(); }, []);

  if (crmLoading && crmContacts.length === 0) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center space-y-3">
          <div className={`w-8 h-8 border-2 border-t-transparent ${ac(accentColor, 'badge').replace('bg-', 'border-')} rounded-full animate-spin mx-auto`} />
          <p className="text-zinc-600 font-mono text-[10px] uppercase tracking-widest">Chargement CRM…</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-4 font-mono">

      {/* ── KPI STRIP ───────────────────────────────────────────────────── */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-2.5">
        {[
          { label: profile.vocabulary.contacts, value: kpis.total,     icon: User,        color: 'text-zinc-300',   sub: `${kpis.actifs} actifs`                       },
          { label: stages.at(-2)?.label ?? 'Convertis', value: kpis.converted, icon: Zap, color: 'text-emerald-400', sub: kpis.total > 0 ? `${Math.round(kpis.converted/kpis.total*100)}%` : '—' },
          { label: profile.crmConfig.scoreLabel, value: kpis.avgScore, icon: Star,        color: kpis.avgScore >= 70 ? 'text-emerald-400' : kpis.avgScore >= 40 ? 'text-amber-400' : 'text-red-400', sub: 'moyen' },
          { label: 'Étapes',                     value: stages.length,  icon: Layers,      color: ac(accentColor, 'text'), sub: profile.vocabulary.pipeline.split(' ').slice(0,2).join(' ') },
        ].map(({ label, value, icon: Icon, color, sub }) => (
          <div key={label} className="border border-zinc-800 bg-black/40 p-4 relative overflow-hidden group">
            {/* accent bar left */}
            <div className={`absolute left-0 top-0 bottom-0 w-0.5 ${ac(accentColor, 'badge')} opacity-0 group-hover:opacity-100 transition-opacity`} />
            <div className="flex items-center justify-between mb-1.5">
              <span className="text-[9px] uppercase tracking-widest text-zinc-600">{label}</span>
              <Icon size={12} className={`${color} opacity-40`} />
            </div>
            <div className={`text-2xl font-black font-mono tabular-nums ${color}`}>{value}</div>
            <div className="text-[9px] text-zinc-700 mt-0.5 truncate">{sub}</div>
          </div>
        ))}
      </div>

      {/* ── TOOLBAR ─────────────────────────────────────────────────────── */}
      <div className="flex flex-wrap items-center gap-2">
        {/* Recherche */}
        <div className="relative flex-1 min-w-36">
          <Search size={11} className="absolute left-3 top-1/2 -translate-y-1/2 text-zinc-600" />
          <input
            value={crmSearch}
            onChange={e => setCrmSearch(e.target.value)}
            placeholder={`Rechercher un ${profile.vocabulary.contact.toLowerCase()}…`}
            className="w-full bg-black/50 border border-zinc-800 pl-8 pr-3 py-2 text-[10px] text-zinc-200 placeholder-zinc-700 focus:border-zinc-600 outline-none"
          />
        </div>

        {/* Filtre étape */}
        <div className="relative">
          <Filter size={10} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-zinc-600" />
          <select
            value={crmStageFilter}
            onChange={e => setCrmStageFilter(e.target.value)}
            className="bg-black/50 border border-zinc-800 pl-7 pr-6 py-2 text-[10px] text-zinc-300 focus:border-zinc-600 outline-none appearance-none"
          >
            <option value="all">Toutes étapes</option>
            {stages.map(s => <option key={s.id} value={s.id}>{s.icon} {s.label}</option>)}
          </select>
          <ChevronDown size={9} className="absolute right-2 top-1/2 -translate-y-1/2 text-zinc-600 pointer-events-none" />
        </div>

        {/* Vue Kanban / Liste */}
        <div className="flex border border-zinc-800 overflow-hidden">
          {[
            { id: 'kanban', label: 'Kanban' },
            { id: 'list',   label: 'Liste'  },
          ].map((v, i) => (
            <button key={v.id} onClick={() => setView(v.id)}
                    className={`px-3 py-2 text-[9px] uppercase tracking-wider transition-colors ${i > 0 ? 'border-l border-zinc-800' : ''} ${
                      view === v.id
                        ? ac(accentColor, 'tab')
                        : 'text-zinc-500 hover:text-zinc-300'
                    }`}>
              {v.label}
            </button>
          ))}
        </div>

        {/* Compact global (Kanban only) */}
        {view === 'kanban' && (
          <button
            onClick={() => setGlobalCompact(g => !g)}
            title={globalCompact ? 'Mode normal' : 'Mode compact global'}
            className={`px-3 py-2 text-[9px] uppercase tracking-wider border transition-colors ${
              globalCompact
                ? `border-zinc-700 ${ac(accentColor, 'text')} bg-zinc-800/40`
                : 'border-zinc-800 text-zinc-600 hover:text-zinc-300'
            }`}
          >
            <Layers size={12} />
          </button>
        )}

        {/* Nouveau contact */}
        <button
          onClick={() => setShowNew(true)}
          className={`flex items-center gap-1.5 px-3 py-2 ${ac(accentColor, 'btn')} text-white text-[9px] uppercase tracking-widest font-bold transition-colors`}
        >
          <Plus size={11} />
          <span className="hidden sm:inline">Nouveau</span>
        </button>
      </div>

      {/* Compteur */}
      <div className="text-[9px] text-zinc-700 uppercase tracking-widest -mt-1">
        {filtered.length} {profile.vocabulary.contact.toLowerCase()}{filtered.length > 1 ? 's' : ''}
        {crmSearch && <span className="text-zinc-600"> · "{crmSearch}"</span>}
      </div>

      {/* ── KANBAN ──────────────────────────────────────────────────────── */}
      {view === 'kanban' && (
        <div className="overflow-x-auto pb-2 -mx-1 px-1" style={{ scrollbarWidth: 'none' }}>
          <div className="flex gap-2 min-w-max">
            {stages.map(stage => (
              <KanbanColumn
                key={stage.id}
                stage={stage}
                contacts={byStage[stage.id] ?? []}
                profile={profile}
                accentColor={accentColor}
                onSelect={setSelectedContact}
                onMove={moveCrmContactStage}
                onDelete={deleteCrmContact}
              />
            ))}
          </div>
          {/* hint mode */}
          <div className="mt-2 text-[8px] text-zinc-800 font-mono uppercase tracking-widest text-center">
            Clic sur l'en-tête pour changer le mode colonne · ● complet &nbsp;● compact &nbsp;● réduit
          </div>
        </div>
      )}

      {/* ── LISTE ───────────────────────────────────────────────────────── */}
      {view === 'list' && (
        <div className="border border-zinc-800 bg-black/30 overflow-hidden">
          {/* Header */}
          <div className="grid grid-cols-12 gap-2 px-4 py-2.5 border-b border-zinc-800/80 text-[8px] uppercase tracking-widest text-zinc-700">
            <span className="col-span-3">Nom</span>
            <span className="col-span-3">Contact</span>
            <span className="col-span-2">{profile.vocabulary.company}</span>
            <span className="col-span-2">Étape</span>
            <span className="col-span-1">Score</span>
            <span className="col-span-1 text-right">···</span>
          </div>

          {/* Rows */}
          <div className="divide-y divide-zinc-800/30 overflow-y-auto scrollbar-none" style={{ maxHeight: '36rem' }}>
            {filtered.map(ct => {
              const stage = stages.find(s => s.id === ct.stage) ?? stages[0];
              return (
                <div
                  key={ct._id ?? ct.id}
                  onClick={() => setSelectedContact(ct)}
                  className="grid grid-cols-12 gap-2 px-4 py-3 hover:bg-zinc-900/40 cursor-pointer transition-colors items-center group"
                >
                  <div className="col-span-3 min-w-0">
                    <div className="text-[10px] font-bold text-zinc-200 truncate">
                      {ct.prenom ? `${ct.prenom} ${ct.nom}` : ct.nom}
                    </div>
                    {ct.notes?.length > 0 && (
                      <div className="text-[8px] text-zinc-700 flex items-center gap-0.5 mt-0.5">
                        <FileText size={7} /> {ct.notes.length}
                      </div>
                    )}
                  </div>
                  <div className="col-span-3 min-w-0">
                    <div className="text-[9px] text-zinc-500 font-mono truncate">{ct.email}</div>
                    <div className="text-[8px] text-zinc-700 font-mono truncate mt-0.5">{ct.telephone}</div>
                  </div>
                  <div className="col-span-2 min-w-0">
                    <span className="text-[9px] text-zinc-500 truncate font-mono">{ct.entreprise || '—'}</span>
                  </div>
                  <div className="col-span-2">
                    <span className={`inline-flex items-center gap-1 text-[8px] px-1.5 py-0.5 font-mono ${sc(stage.color, 'pill')}`}>
                      {stage.icon} {stage.label}
                    </span>
                  </div>
                  <div className="col-span-1">
                    <ScoreBar score={ct.score ?? 0} size="sm" />
                  </div>
                  <div className="col-span-1 flex justify-end">
                    <button
                      onClick={e => { e.stopPropagation(); deleteCrmContact(ct._id ?? ct.id); }}
                      className="p-1 text-zinc-800 group-hover:text-zinc-600 hover:!text-red-400 transition-colors"
                    >
                      <Trash2 size={10} />
                    </button>
                  </div>
                </div>
              );
            })}

            {filtered.length === 0 && (
              <div className="py-16 text-center">
                <User size={28} className="text-zinc-800 mx-auto mb-2" />
                <p className="text-zinc-700 text-[10px] uppercase tracking-widest">
                  Aucun {profile.vocabulary.contact.toLowerCase()}
                </p>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── DRAWER ──────────────────────────────────────────────────────── */}
      {selectedContact && (
        <ContactDrawer
          contact={selectedContact}
          profile={profile}
          accentColor={accentColor}
          onClose={clearSelectedContact}
          onUpdate={updateCrmContact}
          onAddNote={addCrmNote}
          onMove={moveCrmContactStage}
        />
      )}

      {/* ── MODAL NOUVEAU ───────────────────────────────────────────────── */}
      {showNew && (
        <NewContactModal
          profile={profile}
          accentColor={accentColor}
          onClose={() => setShowNew(false)}
          onCreate={createCrmContact}
        />
      )}
    </div>
  );
}

export default CRMPanel;